import { ToolDef, ToolAnswer, ToolContext } from '../mcp-types';
import { Entity, TypeData } from '../../../model/entity.model';

/** A field as returned by read_project — structure only, never record values. */
export interface ReadProjectField {
  name: string;
  type: string;
  settings: ReadProjectFieldSettings;
}

// No isUnique: the flag is stored on some fields, but Zavo offers no unique setting a builder can use.
export interface ReadProjectFieldSettings {
  primaryKey: boolean;
  multiple?: boolean;
  description?: string;
  referenceEntityName?: string;
}

export interface ReadProjectLink {
  targetEntityId: string;
  fieldName: string;
}

export interface ReadProjectEntity {
  id: string;
  name: string;
  kind: string;
  fields: ReadProjectField[];
  links: ReadProjectLink[];
}

export interface ReadProjectPage {
  id: string;
  name: string;
}

export interface ReadProjectWorkspace {
  id: string;
  name: string;
}

export interface ReadProjectResult {
  entities?: ReadProjectEntity[];
  pages?: ReadProjectPage[];
  workspaces?: ReadProjectWorkspace[];
}

const KNOWN_SUBJECTS = ['entities', 'pages', 'workspaces'] as const;

/** Extract structure-only fields from a TypeData array — NEVER include defaultValue or predefinedData. */
function extractFields(fields: TypeData[]): ReadProjectField[] {
  return fields.map(f => {
    const settings: ReadProjectFieldSettings = {
      primaryKey: f.primaryKey,
    };
    if (f.multiple) settings.multiple = f.multiple;
    if (f.description) settings.description = f.description;
    if (f.referenceEntityName) settings.referenceEntityName = f.referenceEntityName;
    return { name: f.name, type: f.type, settings };
  });
}

/** Extract links (ENTITY_REFERENCE fields) from a TypeData array. */
function extractLinks(fields: TypeData[]): ReadProjectLink[] {
  const links: ReadProjectLink[] = [];
  for (const f of fields) {
    if (f.type === 'ENTITY_REFERENCE' && f.referenceEntityName) {
      links.push({ targetEntityId: f.referenceEntityName, fieldName: f.name });
    }
  }
  return links;
}

function mapEntity(entity: Entity): ReadProjectEntity {
  return {
    id: entity.id,
    name: entity.name,
    kind: entity.type || 'TABLE',
    fields: extractFields(entity.data || []),
    links: extractLinks(entity.data || []),
  };
}

export function makeReadProjectTool(
  getEntities: () => Entity[],
  getPages: () => { id: string; name: string }[],
  getWorkspaces: () => { id: string; name: string }[],
): ToolDef {
  return {
    name: 'read_project',
    kind: 'read',
    description: [
      'Returns the shape of the open project: its entities with their fields, types and links, its',
      'pages, and its workspaces.',
      'Call it before changing anything, and to find the ids that navigate, show and add_entity_field take.',
      'Narrow it with "subject": kind "entities", "pages" or "workspaces", and an entity id with kind "entities".',
      'It changes nothing. Returns names, types and links only, never the values stored in records.',
    ].join(' '),
    inputSchema: {
      type: 'object',
      properties: {
        subject: {
          type: 'object',
          description: 'Optional filter. "kind" is one of entities, pages, workspaces. "id" narrows to one entity.',
          properties: {
            kind: { type: 'string', enum: ['entities', 'pages', 'workspaces'] },
            id: { type: 'string', description: 'Entity id — only meaningful when kind is "entities".' },
          },
          required: ['kind'],
          additionalProperties: false,
        },
      },
      additionalProperties: false,
    },

    async run(input: Record<string, unknown>, ctx: ToolContext): Promise<ToolAnswer> {
      const subject = input['subject'] as { kind?: string; id?: string } | undefined;

      if (subject) {
        const kind = subject.kind;
        if (!kind || !KNOWN_SUBJECTS.includes(kind as any)) {
          return {
            outcome: 'refused',
            refusal: `Unknown subject '${kind}'. Known subjects: ${KNOWN_SUBJECTS.join(', ')}.`,
          };
        }

        if (kind === 'entities') {
          const entities = getEntities();
          if (subject.id) {
            const entity = entities.find(e => e.id === subject.id);
            if (!entity) {
              return { outcome: 'refused', refusal: `Entity '${subject.id}' not found in this project. Call read_project without an id to list the entities.` };
            }
            return { outcome: 'done', changed: [], result: { entities: [mapEntity(entity)] } };
          }
          return { outcome: 'done', changed: [], result: { entities: entities.map(mapEntity) } };
        }

        if (kind === 'pages') {
          const pages = getPages();
          return { outcome: 'done', changed: [], result: { pages: pages.map(p => ({ id: p.id, name: p.name })) } };
        }

        if (kind === 'workspaces') {
          const workspaces = getWorkspaces();
          return { outcome: 'done', changed: [], result: { workspaces: workspaces.map(w => ({ id: w.id, name: w.name })) } };
        }
      }

      // No filter — return everything.
      const entities = getEntities();
      const pages = getPages();
      const workspaces = getWorkspaces();

      const result: ReadProjectResult = {
        entities: entities.map(mapEntity),
        pages: pages.map(p => ({ id: p.id, name: p.name })),
        workspaces: workspaces.map(w => ({ id: w.id, name: w.name })),
      };

      return { outcome: 'done', changed: [], result };
    },
  };
}
